import { useRef, useCallback, useEffect, useState } from 'react';
import { MagnifyingGlassPlus, MagnifyingGlassMinus, Timer, Repeat, RepeatOnce, ArrowsLeftRight, Eye, EyeSlash, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';
import { useTimelineStore } from '../../stores/timelineStore';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useSceneStore } from '../../stores/sceneStore';
import { useAnimationController } from '../../hooks/useAnimationController';
import { getSpineManager } from '../../lib/spine-manager';
import { Tooltip } from '../ui/tooltip';
import type { Layer } from '@spine-workspace/shared';

const NAME_COL_W = 176; // px
const ROW_H = 36; // px per layer row
const RULER_H = 24; // px for time ruler
const TOTAL_DURATION_MIN = 4; // minimum total timeline seconds shown

/** Round to a "nice" time interval for ruler ticks */
function niceInterval(pps: number): number {
  const targetPx = 80;
  const raw = targetPx / pps;
  if (raw <= 0.1) return 0.1;
  if (raw <= 0.25) return 0.25;
  if (raw <= 0.5) return 0.5;
  if (raw <= 1) return 1;
  if (raw <= 2) return 2;
  if (raw <= 5) return 5;
  return 10;
}

/** Layer color palette — cycles through accents */
const LAYER_COLORS = [
  '#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626',
  '#0891b2', '#7c3aed', '#16a34a', '#9333ea', '#ea580c',
];

function layerColor(idx: number): string {
  return LAYER_COLORS[idx % LAYER_COLORS.length];
}

interface TimelineLayerRowProps {
  layer: Layer;
  idx: number;
  duration: number;
  totalDuration: number;
  pps: number;
  trackW: number;
  currentTime: number;
  selected: boolean;
  isSoloed: boolean;
  onSelect: () => void;
  onScrub: (time: number) => void;
  onToggleVisible: () => void;
  onToggleSolo: () => void;
  keyframeTimes?: number[];
}

function TimelineLayerRow({
  layer, idx, duration, totalDuration, pps, trackW, currentTime, selected, isSoloed, onSelect, onScrub, onToggleVisible, onToggleSolo, keyframeTimes,
}: TimelineLayerRowProps) {
  const color = layerColor(idx);
  const trackRef = useRef<HTMLDivElement>(null);

  const handleTrackPointer = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onScrub(ratio * totalDuration);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [totalDuration, onScrub]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (e.buttons !== 1) return;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onScrub(ratio * totalDuration);
  }, [totalDuration, onScrub]);

  // Calculate animation block positions
  const blocks: { left: number; width: number; label: string; loop: boolean }[] = [];

  if (layer.animation.tracks.length > 0) {
    layer.animation.tracks.forEach((track, ti) => {
      if (!track.name) return;
      const animDur = duration / layer.animation.tracks.length; // approximate per-track
      const blockW = Math.max(animDur * pps, 8);

      if (track.loop && animDur > 0) {
        // Show looping pattern: repeating bars
        for (let start = 0; start < trackW; start += blockW) {
          blocks.push({
            left: start,
            width: Math.min(blockW - 2, trackW - start),
            label: ti === 0 ? track.name : '',
            loop: true,
          });
        }
      } else {
        blocks.push({
          left: 0,
          width: Math.min(blockW - 2, trackW),
          label: track.name,
          loop: false,
        });
      }
    });
  } else if (duration > 0) {
    // No tracks but has duration — show empty loop block
    const blockW = Math.max(duration * pps, 16);
    for (let start = 0; start < trackW; start += blockW) {
      blocks.push({
        left: start,
        width: Math.min(blockW - 2, trackW - start),
        label: '',
        loop: true,
      });
    }
  }

  // Playhead position
  const playheadLeft = totalDuration > 0 ? (currentTime / totalDuration) * trackW : 0;

  return (
    <div
      className={`flex border-b border-border-subtle/50 ${selected ? 'bg-accent-muted/30' : 'hover:bg-bg-hover/20'} transition-colors`}
      style={{ height: ROW_H }}
      data-testid={`timeline-row-${layer.id}`}
    >
      {/* Name column */}
      <div
        className={`flex items-center gap-1.5 px-2 shrink-0 cursor-pointer select-none ${selected ? 'text-text-primary' : 'text-text-secondary'}`}
        style={{ width: NAME_COL_W }}
        onClick={onSelect}
        data-testid={`timeline-layer-name-${layer.id}`}
      >
        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-sans truncate flex-1 min-w-0" title={layer.name}>
          {layer.name}
        </span>
        {/* Per-layer inline controls */}
        <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Tooltip content={layer.visible ? 'Hide layer' : 'Show layer'}>
            <button
              data-testid={`timeline-vis-${layer.id}`}
              onClick={onToggleVisible}
              className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
                layer.visible ? 'text-text-tertiary hover:text-text-primary' : 'text-text-disabled hover:text-text-tertiary'
              }`}
            >
              {layer.visible ? <Eye size={11} /> : <EyeSlash size={11} />}
            </button>
          </Tooltip>
          <Tooltip content={isSoloed ? 'Unsolo' : 'Solo (hear only this layer)'}>
            <button
              data-testid={`timeline-solo-${layer.id}`}
              onClick={onToggleSolo}
              className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
                isSoloed ? 'text-accent' : 'text-text-tertiary hover:text-text-primary'
              }`}
            >
              {isSoloed ? <SpeakerHigh size={11} /> : <SpeakerSlash size={11} />}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Track area */}
      <div
        ref={trackRef}
        className="flex-1 relative overflow-hidden cursor-crosshair"
        style={{ height: ROW_H }}
        onPointerDown={handleTrackPointer}
        onPointerMove={handlePointerMove}
      >
        {/* Animation blocks */}
        {blocks.map((block, i) => (
          <div
            key={i}
            className="absolute top-[6px] rounded-sm"
            style={{
              left: block.left,
              width: block.width,
              height: ROW_H - 12,
              backgroundColor: color,
              opacity: layer.visible ? 0.75 : 0.3,
            }}
          >
            {block.label && block.width > 32 && (
              <span className="absolute inset-0 flex items-center px-1.5 text-[9px] text-white font-mono truncate pointer-events-none">
                {block.label}
              </span>
            )}
            {block.loop && block.width > 12 && (
              <div
                className="absolute right-0 top-0 bottom-0 w-[3px] opacity-40"
                style={{ backgroundColor: 'white' }}
              />
            )}
          </div>
        ))}

        {/* Keyframe diamonds (dope sheet) */}
        {keyframeTimes && keyframeTimes.map((t, ki) => {
          const left = totalDuration > 0 ? (t / totalDuration) * trackW : 0;
          // Only render if within visible range (rough cull)
          if (left < -8 || left > trackW + 8) return null;
          return (
            <div
              key={ki}
              className="absolute pointer-events-none z-[6]"
              style={{
                left: left - 4,
                top: '50%',
                marginTop: -4,
                width: 8,
                height: 8,
                backgroundColor: color,
                opacity: 0.95,
                transform: 'rotate(45deg)',
                borderRadius: 1,
              }}
            />
          );
        })}

        {/* Playhead line */}
        <div
          className="absolute top-0 bottom-0 w-px z-10 pointer-events-none"
          style={{ left: playheadLeft, backgroundColor: '#ef4444' }}
        />
      </div>
    </div>
  );
}

export default function TimelinePanel() {
  const { visible, height, pixelsPerSecond, setHeight, zoomIn, zoomOut } = useTimelineStore();
  const { currentFrame, fps, isPlaying, playbackMode, setPlaybackMode } = usePlaybackStore();
  const { layers, selectedLayerId, selectLayer, soloedLayerId, soloLayer, toggleLayerVisibility } = useSceneStore();
  const { handleSeek } = useAnimationController();

  const resizeRef = useRef<HTMLDivElement>(null);
  const trackScrollRef = useRef<HTMLDivElement>(null);
  const [layerDurations, setLayerDurations] = useState<Record<string, number>>({});
  const [layerKeyframes, setLayerKeyframes] = useState<Record<string, number[]>>({});
  const animFrameRef = useRef<number>();

  // Poll layer durations from SpineManager (rAF loop)
  useEffect(() => {
    function update() {
      const mgr = getSpineManager();
      const durations: Record<string, number> = {};
      for (const layer of layers) {
        const d = mgr.getMaxAnimationDuration(layer.id);
        if (d > 0) durations[layer.id] = d;
      }
      setLayerDurations((prev) => {
        const same = Object.keys(durations).every((k) => prev[k] === durations[k]);
        return same ? prev : durations;
      });
      animFrameRef.current = requestAnimationFrame(update);
    }
    animFrameRef.current = requestAnimationFrame(update);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [layers]);

  // Poll keyframe times once per layer change (static metadata, no rAF needed)
  useEffect(() => {
    const mgr = getSpineManager();
    const ktimes: Record<string, number[]> = {};
    for (const layer of layers) {
      const allTimes: number[] = [];
      // Check up to 4 tracks per layer
      for (let ti = 0; ti < 4; ti++) {
        const times = mgr.getKeyframeTimes(layer.id, ti);
        allTimes.push(...times);
      }
      if (allTimes.length > 0) {
        ktimes[layer.id] = [...new Set(allTimes)].sort((a, b) => a - b);
      }
    }
    setLayerKeyframes(ktimes);
  }, [layers]);

  const currentTime = fps > 0 ? currentFrame / fps : 0;

  // Total timeline duration = max of all layer durations, min 4s
  const maxDuration = Object.values(layerDurations).reduce((m, d) => Math.max(m, d), 0);
  const totalDuration = Math.max(maxDuration, TOTAL_DURATION_MIN);
  const trackW = totalDuration * pixelsPerSecond;

  // Auto-scroll playhead into view when playing
  useEffect(() => {
    if (!isPlaying || !trackScrollRef.current || totalDuration <= 0) return;
    const el = trackScrollRef.current;
    const playheadPx = (currentTime / totalDuration) * trackW;
    const visibleLeft = el.scrollLeft;
    const visibleRight = visibleLeft + el.clientWidth;
    if (playheadPx > visibleRight - 40 || playheadPx < visibleLeft) {
      el.scrollLeft = Math.max(0, playheadPx - el.clientWidth / 4);
    }
  }, [currentTime, isPlaying, totalDuration, trackW]);

  // Resize handle drag
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = height;
    const onMove = (ev: MouseEvent) => {
      setHeight(startH - (ev.clientY - startY));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [height, setHeight]);

  // Ruler scrub
  const handleRulerScrub = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetTime = ratio * totalDuration;
    const frame = Math.round(targetTime * fps);
    handleSeek(frame);
  }, [totalDuration, fps, handleSeek]);

  function handleLayerScrub(time: number) {
    const frame = Math.round(time * fps);
    handleSeek(frame);
  }

  if (!visible) return null;

  // Ruler ticks
  const interval = niceInterval(pixelsPerSecond);
  const ticks: { left: number; label: string; major: boolean }[] = [];
  for (let t = 0; t <= totalDuration + interval; t += interval) {
    const left = t * pixelsPerSecond;
    const major = Math.round(t) === t;
    ticks.push({ left, label: major ? `${Math.round(t)}s` : `${t.toFixed(2)}s`, major });
  }

  return (
    <div
      className="bg-bg-surface border-t-2 border-border-subtle flex flex-col select-none overflow-hidden"
      style={{ height }}
      data-testid="timeline-panel"
    >
      {/* Resize handle */}
      <div
        ref={resizeRef}
        onMouseDown={startResize}
        className="h-[3px] cursor-row-resize shrink-0 hover:bg-accent/40 transition-colors group"
        data-testid="timeline-resize-handle"
      />

      {/* Panel header */}
      <div className="flex items-center gap-2 px-3 py-1 border-b border-border-subtle shrink-0 h-8">
        <Timer size={12} className="text-accent shrink-0" />
        <span className="text-[11px] font-sans font-semibold text-accent tracking-wide uppercase">Timeline</span>
        <span className="text-[10px] text-text-tertiary font-mono">
          {currentTime.toFixed(2)}s / {totalDuration.toFixed(1)}s
        </span>
        <div className="ml-auto flex items-center gap-1">
          {/* Playback mode: loop / once / ping-pong */}
          <div className="flex items-center border border-border rounded-md overflow-hidden mr-1">
            {([
              { mode: 'loop',      icon: <Repeat size={11} />,         title: 'Loop' },
              { mode: 'once',      icon: <RepeatOnce size={11} />,     title: 'Play once' },
              { mode: 'ping-pong', icon: <ArrowsLeftRight size={11} />, title: 'Ping-pong' },
            ] as const).map(({ mode, icon, title }) => (
              <Tooltip key={mode} content={title}>
                <button
                  data-testid={`playback-mode-${mode}`}
                  onClick={() => setPlaybackMode(mode)}
                  className={`w-6 h-5 flex items-center justify-center transition-colors ${
                    playbackMode === mode
                      ? 'bg-accent text-white'
                      : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  {icon}
                </button>
              </Tooltip>
            ))}
          </div>
          <Tooltip content="Zoom out timeline">
            <button
              onClick={zoomOut}
              data-testid="timeline-zoom-out"
              className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
            >
              <MagnifyingGlassMinus size={12} />
            </button>
          </Tooltip>
          <span className="text-[10px] font-mono text-text-tertiary w-10 text-center">
            {pixelsPerSecond}px/s
          </span>
          <Tooltip content="Zoom in timeline">
            <button
              onClick={zoomIn}
              data-testid="timeline-zoom-in"
              className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
            >
              <MagnifyingGlassPlus size={12} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Main body: name column + scrollable track area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Name column header */}
        <div
          className="shrink-0 flex flex-col border-r border-border-subtle"
          style={{ width: NAME_COL_W }}
        >
          <div
            className="flex items-center px-3 border-b border-border-subtle text-[10px] text-text-tertiary uppercase font-sans font-semibold tracking-wide"
            style={{ height: RULER_H }}
          >
            Layer
          </div>
          {/* Layer names (scroll sync handled by shared container) */}
          <div className="flex-1 overflow-hidden">
            {layers.length === 0 ? (
              <div className="p-3 text-xs text-text-tertiary font-sans">No layers</div>
            ) : null}
          </div>
        </div>

        {/* Scrollable track area */}
        <div
          ref={trackScrollRef}
          className="flex-1 overflow-x-auto overflow-y-auto"
          data-testid="timeline-track-area"
        >
          <div style={{ width: Math.max(trackW + 40, 400), minHeight: '100%' }}>
            {/* Time ruler */}
            <div
              className="sticky top-0 z-20 bg-bg-surface border-b border-border-subtle cursor-crosshair relative"
              style={{ height: RULER_H, width: Math.max(trackW + 40, 400) }}
              onPointerDown={handleRulerScrub}
              onPointerMove={(e) => { if (e.buttons === 1) handleRulerScrub(e); }}
              data-testid="timeline-ruler"
            >
              {ticks.map((tick) => (
                <div
                  key={tick.left}
                  className="absolute top-0 flex flex-col items-center pointer-events-none"
                  style={{ left: tick.left }}
                >
                  <div
                    className={`w-px ${tick.major ? 'h-3 bg-border' : 'h-2 bg-border-subtle'}`}
                    style={{ marginTop: tick.major ? 0 : 4 }}
                  />
                  {tick.major && (
                    <span className="text-[9px] font-mono text-text-tertiary mt-0.5">
                      {tick.label}
                    </span>
                  )}
                </div>
              ))}
              {/* Ruler playhead */}
              {totalDuration > 0 && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-red-500 z-10 pointer-events-none"
                  style={{ left: (currentTime / totalDuration) * trackW }}
                />
              )}
            </div>

            {/* Layer track rows */}
            {layers.map((layer, idx) => (
              <TimelineLayerRow
                key={layer.id}
                layer={layer}
                idx={idx}
                duration={layerDurations[layer.id] ?? 0}
                totalDuration={totalDuration}
                pps={pixelsPerSecond}
                trackW={trackW}
                currentTime={currentTime}
                selected={layer.id === selectedLayerId}
                isSoloed={soloedLayerId === layer.id}
                onSelect={() => selectLayer(layer.id)}
                onScrub={handleLayerScrub}
                onToggleVisible={() => toggleLayerVisibility(layer.id)}
                onToggleSolo={() => soloLayer(layer.id)}
                keyframeTimes={layerKeyframes[layer.id]}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
